import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export type EngageRole = 'ADMIN' | 'PUBLISH' | 'WRITE' | 'VIEW';
/** Effective access shown in the permissions UI. UNALLOCATED = synced from JT
 *  but not yet given an Engage role by an admin. */
export type EffectiveEngageRole = EngageRole | 'UNALLOCATED';

export interface OrgUser {
  jt_user_id: string;
  jt_account_id?: string | null;
  name: string;
  email: string;
  /** The user's role in JobTetris (the primary app). */
  role: string;
  /** Explicit Engage allocation, or null when unallocated. */
  engage_role?: EngageRole | null;
  /** JobTetris's per-user "Engage access" toggle (who can open Engage at all).
   *  null = not known yet. */
  engage_access?: boolean | null;
  /** Resolved Engage access: explicit role, JT-admin auto-admin, or UNALLOCATED. */
  effective_engage_role?: EffectiveEngageRole;
  has_active_mailbox?: boolean;
  sms_number_count?: number;
  /** Server-computed: the caller may start a Manage as session for this user. */
  can_manage_as?: boolean;
}

export function useOrgUsers() {
  return useQuery<OrgUser[]>({
    queryKey: ['settings', 'org-users'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: OrgUser[] }>('/settings/org-users');
      return data.data;
    },
    staleTime: 60_000,
  });
}

/**
 * Allocate (or clear) a synced user's Engage role. There is no invite flow —
 * access is a sync from JobTetris — so this only re-roles users who already
 * have a JT (and therefore Engage) account. Pass role: null to reset to
 * "unallocated".
 */
export function useSetEngageRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ jtUserId, role }: { jtUserId: string; role: EngageRole | null }) => {
      const { data } = await apiClient.patch<{ data: OrgUser }>(
        `/settings/org-users/${jtUserId}/role`,
        { role },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'org-users'] });
      // An admin can re-role themselves — refresh the live session role too.
      qc.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
  });
}
